import { intersection, first, throttleWithValue, clamp, sortBy, shuffle, pushToMapKey, ctx, always, inc, add, ago, take, chunks, MINUTE, HOUR, DAY, WEEK, } from "@welshman/lib";
import { getFilterId, isRelayUrl, isOnionUrl, isLocalUrl, isIPAddress, isShareableRelayUrl, COMMENT, PROFILE, RELAYS, INBOX_RELAYS, FOLLOWS, WRAP, getReplyTags, getCommentTags, getPubkeyTagValues, normalizeRelayUrl, } from "@welshman/util";
import { pubkey } from "./session.js";
import { relaySelectionsByPubkey, inboxRelaySelectionsByPubkey, getReadRelayUrls, getWriteRelayUrls, getRelayUrls, } from "./relaySelections.js";
import { relays, relaysByUrl } from "./relays.js";
export const INDEXED_KINDS = [PROFILE, RELAYS, INBOX_RELAYS, FOLLOWS];
export var RelayMode;
(function (RelayMode) {
    RelayMode["Read"] = "read";
    RelayMode["Write"] = "write";
    RelayMode["Inbox"] = "inbox";
})(RelayMode || (RelayMode = {}));
const makeSelection = (relays, weight = 1) => ({
    relays: relays.map(normalizeRelayUrl),
    weight,
});
export const addNoFallbacks = (count, limit) => 0;
export const addMinimalFallbacks = (count, limit) => (count > 0 ? 0 : 1);
export const addMaximalFallbacks = (count, limit) => limit - count;
export class Router {
    options;
    constructor(options) {
        this.options = options;
    }
    // Utilities derived from options
    getRelaysForPubkey = (pubkey, mode) => this.options.getPubkeyRelays?.(pubkey, mode) || [];
    getRelaysForPubkeys = (pubkeys, mode) => pubkeys.map(pubkey => this.getRelaysForPubkey(pubkey, mode));
    getRelaysForUser = (mode) => {
        const pubkey = this.options.getUserPubkey?.();
        return pubkey ? this.getRelaysForPubkey(pubkey, mode) : [];
    };
    // Utilities for creating scenarios
    scenario = (selections) => new RouterScenario(this, selections);
    merge = (scenarios) => this.scenario(scenarios.flatMap((scenario) => scenario.selections));
    // Routing scenarios
    FromRelays = (relays) => this.scenario([makeSelection(relays)]);
    ForUser = () => this.FromRelays(this.getRelaysForUser(RelayMode.Read));
    FromUser = () => this.FromRelays(this.getRelaysForUser(RelayMode.Write));
    UserInbox = () => this.FromRelays(this.getRelaysForUser(RelayMode.Inbox)).policy(addNoFallbacks);
    ForPubkey = (pubkey) => this.FromRelays(this.getRelaysForPubkey(pubkey, RelayMode.Read));
    FromPubkey = (pubkey) => this.FromRelays(this.getRelaysForPubkey(pubkey, RelayMode.Write));
    PubkeyInbox = (pubkey) => this.FromRelays(this.getRelaysForPubkey(pubkey, RelayMode.Inbox)).policy(addNoFallbacks);
    ForPubkeys = (pubkeys) => this.merge(pubkeys.map(pubkey => this.ForPubkey(pubkey)));
    FromPubkeys = (pubkeys) => this.merge(pubkeys.map(pubkey => this.FromPubkey(pubkey)));
    PubkeyInboxes = (pubkeys) => this.merge(pubkeys.map(pubkey => this.PubkeyInbox(pubkey)));
    Event = (event) => this.FromRelays(this.getRelaysForPubkey(event.pubkey, RelayMode.Write));
    Replies = (event) => this.FromRelays(this.getRelaysForPubkey(event.pubkey, RelayMode.Read));
    Quote = (event, value, relays = []) => {
        const tag = event.tags.find(t => t[1] === value);
        const scenarios = [
            this.FromRelays(relays),
            this.ForPubkey(event.pubkey),
            this.FromPubkey(event.pubkey),
        ];
        if (tag?.[2] && isShareableRelayUrl(tag[2])) {
            scenarios.push(this.FromRelays([tag[2]]));
        }
        if (tag?.[3]?.length === 64) {
            scenarios.push(this.FromPubkeys([tag[3]]));
        }
        return this.merge(scenarios);
    };
    EventAncestors = (event, type) => {
        const ancestorTags = event.kind === COMMENT ? getCommentTags(event.tags) : getReplyTags(event.tags);
        const tags = ancestorTags[type] || [];
        return this.scenario(tags.flatMap(([_, value, relay, pubkey]) => {
            const selections = [makeSelection(this.ForUser().getUrls(), 0.5)];
            if (pubkey) {
                selections.push(makeSelection(this.FromPubkey(pubkey).getUrls()));
            }
            if (relay) {
                selections.push(makeSelection([relay], 0.9));
            }
            return selections;
        }));
    };
    EventMentions = (event) => this.EventAncestors(event, "mentions");
    EventParents = (event) => this.EventAncestors(event, "replies");
    EventRoots = (event) => this.EventAncestors(event, "roots");
    PublishEvent = (event) => {
        const pubkeys = getPubkeyTagValues(event.tags);
        return this.merge([
            this.FromPubkey(event.pubkey),
            ...pubkeys.map(pubkey => this.ForPubkey(pubkey).weight(0.5)),
        ]);
    };
}
export class RouterScenario {
    router;
    selections;
    options;
    constructor(router, selections, options = {}) {
        this.router = router;
        this.selections = selections;
        this.options = options;
    }
    clone = (options) => new RouterScenario(this.router, this.selections, { ...this.options, ...options });
    filter = (f) => new RouterScenario(this.router, this.selections.filter(selection => f(selection)), this.options);
    update = (f) => new RouterScenario(this.router, this.selections.map(selection => f(selection)), this.options);
    policy = (policy) => this.clone({ policy });
    limit = (limit) => this.clone({ limit });
    allowLocal = (allowLocal) => this.clone({ allowLocal });
    allowOnion = (allowOnion) => this.clone({ allowOnion });
    allowInsecure = (allowInsecure) => this.clone({ allowInsecure });
    weight = (scale) => this.update(selection => ({ ...selection, weight: selection.weight * scale }));
    getPolicy = () => this.options.policy || addMaximalFallbacks;
    getLimit = () => this.options.limit || this.router.options.getLimit?.() || 3;
    getUrls = () => {
        const limit = this.getLimit();
        const fallbackPolicy = this.getPolicy();
        const relayWeights = new Map();
        const { allowOnion, allowLocal, allowInsecure } = this.options;
        for (const { weight, relays } of this.selections) {
            for (const relay of relays) {
                if (!isRelayUrl(relay))
                    continue;
                if (!allowOnion && isOnionUrl(relay))
                    continue;
                if (!allowLocal && isLocalUrl(relay))
                    continue;
                if (!allowInsecure && relay.startsWith("ws://") && !isOnionUrl(relay))
                    continue;
                relayWeights.set(relay, add(weight, relayWeights.get(relay)));
            }
        }
        const scoreRelay = (relay) => {
            const { getRelayQuality = always(1) } = this.router.options;
            const quality = getRelayQuality(relay);
            const weight = relayWeights.get(relay);
            // Log the weight, since it's a straight count which ends up over-weighting hubs.
            // Also add some random noise so that we'll occasionally pick lower quality/less
            // popular relays.
            return -(quality * inc(Math.log(weight)) * Math.random());
        };
        const relays = take(limit, sortBy(scoreRelay, Array.from(relayWeights.keys()).filter(scoreRelay)));
        const fallbacksNeeded = fallbackPolicy(relays.length, limit);
        const allFallbackRelays = this.router.options.getFallbackRelays();
        const fallbackRelays = shuffle(allFallbackRelays).slice(0, fallbacksNeeded);
        for (const fallbackRelay of fallbackRelays) {
            relays.push(fallbackRelay);
        }
        return relays;
    };
    getUrl = () => first(this.getUrls());
}
// Default router options
export const getRelayQuality = (url) => {
    const relay = relaysByUrl.get().get(url);
    // Skip non-relays entirely
    if (!isRelayUrl(url))
        return 0;
    // If we have recent errors, skip it
    if (relay?.stats) {
        if (relay.stats.recent_errors.filter(n => n > ago(MINUTE)).length > 0)
            return 0;
        if (relay.stats.recent_errors.filter(n => n > ago(HOUR)).length > 3)
            return 0;
        if (relay.stats.recent_errors.filter(n => n > ago(DAY)).length > 10)
            return 0;
        if (relay.stats.recent_errors.filter(n => n > ago(WEEK)).length > 50)
            return 0;
    }
    // Prefer stuff we're connected to
    if (ctx.net.pool.has(url))
        return 1;
    // Prefer stuff we've connected to in the past
    if (relay?.stats)
        return 0.9;
    // If it's not weird url give it an ok score
    if (!isIPAddress(url) && !isLocalUrl(url) && !isOnionUrl(url) && !url.startsWith("ws://")) {
        return 0.8;
    }
    // Default to a "meh" score
    return 0.7;
};
export const getPubkeyRelays = (pubkey, mode) => {
    const $relaySelections = relaySelectionsByPubkey.get();
    const $inboxSelections = inboxRelaySelectionsByPubkey.get();
    switch (mode) {
        case RelayMode.Read:
            return getReadRelayUrls($relaySelections.get(pubkey));
        case RelayMode.Write:
            return getWriteRelayUrls($relaySelections.get(pubkey));
        case RelayMode.Inbox:
            return getRelayUrls($inboxSelections.get(pubkey));
        default:
            return getRelayUrls($relaySelections.get(pubkey));
    }
};
export const getIndexerRelays = () => ctx.app.indexerRelays || getFallbackRelays();
export const getFallbackRelays = throttleWithValue(300, () => sortBy(r => -getRelayQuality(r.url), relays.get())
    .slice(0, 30)
    .map(r => r.url));
export const getSearchRelays = throttleWithValue(300, () => sortBy(r => -getRelayQuality(r.url), relays.get())
    .filter(r => r.profile?.supported_nips?.includes(50))
    .slice(0, 30)
    .map(r => r.url));
export const makeRouter = (options = {}) => new Router({
    getPubkeyRelays,
    getIndexerRelays,
    getFallbackRelays,
    getSearchRelays,
    getRelayQuality,
    getUserPubkey: () => pubkey.get(),
    getLimit: () => 3,
    ...options,
});
export const getFilterSelectionsForSearch = (filter) => {
    if (!filter.search)
        return [];
    const relays = ctx.app.router.options.getSearchRelays?.() || [];
    return [{ filter, scenario: ctx.app.router.FromRelays(relays).weight(10) }];
};
export const getFilterSelectionsForWraps = (filter) => {
    if (!filter.kinds?.includes(WRAP) || filter.authors)
        return [];
    return [
        {
            filter: { ...filter, kinds: [WRAP] },
            scenario: ctx.app.router.UserInbox(),
        },
    ];
};
export const getFilterSelectionsForIndexedKinds = (filter) => {
    const kinds = intersection(INDEXED_KINDS, filter.kinds || []);
    if (kinds.length === 0)
        return [];
    const relays = ctx.app.router.options.getIndexerRelays?.() || [];
    return [
        {
            filter: { ...filter, kinds },
            scenario: ctx.app.router.FromRelays(relays),
        },
    ];
};
export const getFilterSelectionsForAuthors = (filter) => {
    if (!filter.authors)
        return [];
    const chunkCount = clamp([1, 30], Math.round(filter.authors.length / 30));
    return chunks(chunkCount, filter.authors).map(authors => ({
        filter: { ...filter, authors },
        scenario: ctx.app.router.FromPubkeys(authors),
    }));
};
export const getFilterSelectionsForUser = (filter) => [
    { filter, scenario: ctx.app.router.ForUser().weight(0.2) },
];
export const defaultFilterSelectionRules = [
    getFilterSelectionsForSearch,
    getFilterSelectionsForWraps,
    getFilterSelectionsForIndexedKinds,
    getFilterSelectionsForAuthors,
    getFilterSelectionsForUser,
];
export const getFilterSelections = (filters, rules = defaultFilterSelectionRules) => {
    const filtersById = new Map();
    const scenariosById = new Map();
    for (const filter of filters) {
        for (const filterScenario of rules.flatMap(rule => rule(filter))) {
            const id = getFilterId(filterScenario.filter);
            filtersById.set(id, filterScenario.filter);
            pushToMapKey(scenariosById, id, filterScenario.scenario);
        }
    }
    const result = [];
    for (const [id, filter] of filtersById.entries()) {
        const scenario = ctx.app.router.merge(scenariosById.get(id) || []);
        result.push({ filters: [filter], relays: scenario.getUrls() });
    }
    return result;
};
//# sourceMappingURL=router.js.map